import { formatDateRu, formatMoney } from './analyticsCalculations.js'

/**
 * Draws the authentic official "ПВЗ РАСЧЁТ" brand vector logo directly on Canvas.
 * Matches LogoIcon.jsx: turquoise-pink gradient frame, blue-purple histogram bars,
 * and purple-magenta calculator with math symbols (+, −, ×, =).
 */
export function drawOfficialLogo(ctx, x, y, size = 56) {
  ctx.save()
  ctx.translate(x, y)
  const s = size / 512

  // 1. Градиент рамки: бирюза (верх-лево) → розовый (низ-право)
  const gFrame = ctx.createLinearGradient(0, 0, size, size)
  gFrame.addColorStop(0, '#4CEABE')
  gFrame.addColorStop(0.15, '#3AD4D8')
  gFrame.addColorStop(0.35, '#4C90F0')
  gFrame.addColorStop(0.55, '#6870F0')
  gFrame.addColorStop(0.75, '#A050E0')
  gFrame.addColorStop(1, '#F04898')

  // Внешний контур рамки с закруглёнными углами
  drawRoundedRect(ctx, 0, 0, size, size, 14 * (size / 64))
  ctx.fillStyle = gFrame
  ctx.fill()

  // Внутреннее поле (белая заливка)
  const innerPad = 5.5 * (size / 64)
  drawRoundedRect(ctx, innerPad, innerPad, size - innerPad * 2, size - innerPad * 2, 10 * (size / 64))
  ctx.fillStyle = '#FFFFFF'
  ctx.fill()

  // 2. Столбцы гистограммы (голубой → синий → фиолетовый)
  const gBar = ctx.createLinearGradient(0, 0, size, size)
  gBar.addColorStop(0, '#56B0F8')
  gBar.addColorStop(0.4, '#5E90F4')
  gBar.addColorStop(0.7, '#7070F0')
  gBar.addColorStop(1, '#8860E8')
  ctx.fillStyle = gBar

  drawRoundedRect(ctx, 86 * s, 290 * s, 72 * s, 158 * s, 14 * s)
  ctx.fill()
  drawRoundedRect(ctx, 178 * s, 190 * s, 72 * s, 258 * s, 14 * s)
  ctx.fill()
  drawRoundedRect(ctx, 270 * s, 60 * s, 72 * s, 388 * s, 14 * s)
  ctx.fill()

  // 3. Калькулятор (фиолетовый → розовый/маджента)
  const gCalc = ctx.createLinearGradient(300 * s, 300 * s, 500 * s, 500 * s)
  gCalc.addColorStop(0, '#8858F0')
  gCalc.addColorStop(0.4, '#B848D0')
  gCalc.addColorStop(1, '#F04898')
  ctx.fillStyle = gCalc
  drawRoundedRect(ctx, 300 * s, 300 * s, 194 * s, 194 * s, 42 * s)
  ctx.fill()

  // 4. Математические символы калькулятора (белые)
  ctx.strokeStyle = '#FFFFFF'
  ctx.lineWidth = Math.max(2.2, 9 * s)
  ctx.lineCap = 'round'

  // + плюс
  ctx.beginPath()
  ctx.moveTo(370 * s, 358 * s)
  ctx.lineTo(370 * s, 398 * s)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(350 * s, 378 * s)
  ctx.lineTo(390 * s, 378 * s)
  ctx.stroke()

  // − минус
  ctx.beginPath()
  ctx.moveTo(436 * s, 378 * s)
  ctx.lineTo(476 * s, 378 * s)
  ctx.stroke()

  // × умножить
  ctx.beginPath()
  ctx.moveTo(354 * s, 434 * s)
  ctx.lineTo(386 * s, 466 * s)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(386 * s, 434 * s)
  ctx.lineTo(354 * s, 466 * s)
  ctx.stroke()

  // ═ равно
  ctx.lineWidth = Math.max(2, 8 * s)
  ctx.beginPath()
  ctx.moveTo(436 * s, 440 * s)
  ctx.lineTo(476 * s, 440 * s)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(436 * s, 458 * s)
  ctx.lineTo(476 * s, 458 * s)
  ctx.stroke()

  ctx.restore()
}

/**
 * Generates and downloads a clean, beautiful analytical PDF document (A4 portrait).
 * Dynamic multi-page pagination using 2x high-resolution Canvas and jsPDF for 100% Cyrillic support.
 * Automatically flows any number of rows across multiple pages with repeated headers and safe footers.
 */
export async function exportAnalyticsToPdf({ metrics, period, activePvz, organizationName, returnDocOnly = false }) {
  const jspdfModule = await import('jspdf/dist/jspdf.umd.min.js')
  const jsPDF = jspdfModule.jsPDF || jspdfModule.default || jspdfModule

  const pvzName = activePvz?.name || 'ПВЗ'
  const orgName = organizationName || activePvz?.organizationName || 'Организация'
  const dateFrom = formatDateRu(period.startDate)
  const dateTo = formatDateRu(period.endDate)

  // Standard A4 dimensions in px at 2x DPI: 1200 x 1697 (ratio ~ 1 : 1.414)
  const W = 1200
  const H = 1697
  const marginX = 60
  const tableW = W - marginX * 2
  const maxContentY = H - 75 // guaranteed 20px clearance above footer at H - 55

  // List of page canvases created dynamically
  const pages = []

  // Helper to spawn a new canvas page
  function createPage(isContinuation = false) {
    const canvas = document.createElement('canvas')
    canvas.width = W
    canvas.height = H
    const ctx = canvas.getContext('2d')

    // Clean white background
    ctx.fillStyle = '#FFFFFF'
    ctx.fillRect(0, 0, W, H)

    // Top purple accent line
    ctx.fillStyle = '#7C3AED'
    ctx.fillRect(0, 0, W, 8)

    let curY = 46

    if (isContinuation) {
      // Continuation running header with mini brand logo
      drawOfficialLogo(ctx, marginX, curY - 2, 36)

      ctx.fillStyle = '#0F172A'
      ctx.font = 'bold 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
      ctx.fillText('ПВЗ РАСЧЁТ — ДЕТАЛИЗАЦИЯ И ДИНАМИКА', marginX + 48, curY + 16)

      ctx.fillStyle = '#64748B'
      ctx.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
      ctx.fillText(`ПВЗ: ${pvzName}  •  Период: ${dateFrom} — ${dateTo}`, marginX + 48, curY + 36)

      curY += 56

      // Divider line
      ctx.strokeStyle = '#E2E8F0'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(marginX, curY)
      ctx.lineTo(W - marginX, curY)
      ctx.stroke()

      curY += 24
    }

    const pageObj = { canvas, ctx, curY }
    pages.push(pageObj)
    return pageObj
  }

  // ==========================================
  // PAGE 1: ТИТУЛ + КЛЮЧЕВЫЕ KPI + P&L + ЗАТРАТЫ
  // ==========================================
  const page1 = createPage(false)
  const ctx1 = page1.ctx
  let curY = 46

  // Official Brand Vector Logo (56px)
  drawOfficialLogo(ctx1, marginX, curY, 56)

  // Service Title
  ctx1.fillStyle = '#0F172A'
  ctx1.font = 'bold 28px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
  ctx1.fillText('ПВЗ РАСЧЁТ', marginX + 70, curY + 28)

  ctx1.fillStyle = '#64748B'
  ctx1.font = '600 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
  ctx1.fillText('ФИНАНСОВЫЙ АНАЛИТИЧЕСКИЙ ОТЧЁТ (P&L)', marginX + 70, curY + 48)

  // Right metadata block
  const metaW = 400
  const metaH = 76
  const metaX = W - marginX - metaW
  ctx1.fillStyle = '#F8FAFC'
  ctx1.strokeStyle = '#E2E8F0'
  ctx1.lineWidth = 1
  drawRoundedRect(ctx1, metaX, curY - 6, metaW, metaH, 10)
  ctx1.fill()
  ctx1.stroke()

  ctx1.fillStyle = '#334155'
  ctx1.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
  ctx1.fillText(`Организация: ${orgName}`, metaX + 16, curY + 16)
  ctx1.fillText(`Пункт выдачи: ${pvzName}`, metaX + 16, curY + 36)
  ctx1.fillText(`Период отчёта: ${dateFrom} — ${dateTo}`, metaX + 16, curY + 56)

  curY += 86

  // Divider line
  ctx1.strokeStyle = '#E2E8F0'
  ctx1.lineWidth = 1
  ctx1.beginPath()
  ctx1.moveTo(marginX, curY)
  ctx1.lineTo(W - marginX, curY)
  ctx1.stroke()

  curY += 24

  // 4 Key Metrics Cards
  const cardGap = 16
  const cardW = (W - marginX * 2 - cardGap * 3) / 4
  const cardH = 114

  const kpis = [
    {
      title: 'Выручка (Доходы)',
      val: formatMoney(metrics.totalIncome),
      sub: 'Все подтверждённые поступления',
      bg: '#F8FAFC',
      border: '#E2E8F0',
      valColor: '#059669'
    },
    {
      title: 'Расходы (Всего)',
      val: formatMoney(metrics.totalExpensesAll),
      sub: `${metrics.totalIncome > 0 ? ((metrics.totalExpensesAll / metrics.totalIncome) * 100).toFixed(0) : 0}% от выручки`,
      bg: '#F8FAFC',
      border: '#E2E8F0',
      valColor: '#DC2626'
    },
    {
      title: 'Чистая прибыль',
      val: formatMoney(metrics.actualFreeCashFlow),
      sub: metrics.actualFreeCashFlow >= 0 ? 'Операционный остаток' : 'Отрицательный остаток',
      bg: metrics.actualFreeCashFlow >= 0 ? '#ECFDF5' : '#FEF2F2',
      border: metrics.actualFreeCashFlow >= 0 ? '#A7F3D0' : '#FECACA',
      valColor: metrics.actualFreeCashFlow >= 0 ? '#059669' : '#DC2626'
    },
    {
      title: 'Рентабельность',
      val: `${metrics.profitMarginPct.toFixed(1)}%`,
      sub: 'Маржинальность выручки',
      bg: '#F8FAFC',
      border: '#E2E8F0',
      valColor: '#7C3AED'
    }
  ]

  kpis.forEach((kpi, idx) => {
    const kx = marginX + idx * (cardW + cardGap)
    ctx1.fillStyle = kpi.bg
    ctx1.strokeStyle = kpi.border
    ctx1.lineWidth = 1
    drawRoundedRect(ctx1, kx, curY, cardW, cardH, 12)
    ctx1.fill()
    ctx1.stroke()

    ctx1.fillStyle = '#64748B'
    ctx1.font = '700 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
    ctx1.fillText(kpi.title, kx + 16, curY + 28)

    ctx1.fillStyle = kpi.valColor
    ctx1.font = 'bold 24px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
    ctx1.fillText(kpi.val, kx + 16, curY + 68)

    ctx1.fillStyle = '#64748B'
    ctx1.font = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
    ctx1.fillText(kpi.sub, kx + 16, curY + 96)
  })

  curY += cardH + 34

  // Section 1: Фактический денежный поток (P&L)
  ctx1.fillStyle = '#0F172A'
  ctx1.font = 'bold 18px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
  ctx1.fillText('1. Фактический денежный поток (P&L)', marginX, curY)

  ctx1.fillStyle = '#64748B'
  ctx1.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
  ctx1.fillText('Все подтверждённые поступления и статьи затрат за выбранный отчётный период', marginX, curY + 20)

  curY += 34

  const tableHeaderH = 36
  ctx1.fillStyle = '#F1F5F9'
  drawRoundedRect(ctx1, marginX, curY, tableW, tableHeaderH, 6)
  ctx1.fill()

  ctx1.fillStyle = '#475569'
  ctx1.font = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
  ctx1.fillText('СТАТЬЯ ОПЕРАЦИЙ', marginX + 16, curY + 23)
  ctx1.fillText('СУММА', marginX + 700, curY + 23)
  ctx1.fillText('ДОЛЯ ОТ ВЫРУЧКИ', marginX + 900, curY + 23)

  curY += tableHeaderH

  const plRows = [
    { label: 'Выручка Wildberries (Агентское вознаграждение)', amount: `+ ${formatMoney(metrics.totalWbIncome)}`, pct: `${metrics.totalIncome > 0 ? ((metrics.totalWbIncome / metrics.totalIncome) * 100).toFixed(1) : 0}%`, col: '#059669', isBold: false },
    ...(metrics.totalOtherIncome > 0 ? [{ label: 'Прочие доходы пункта выдачи', amount: `+ ${formatMoney(metrics.totalOtherIncome)}`, pct: `${metrics.totalIncome > 0 ? ((metrics.totalOtherIncome / metrics.totalIncome) * 100).toFixed(1) : 0}%`, col: '#059669', isBold: false }] : []),
    { label: 'ИТОГО ВЫРУЧКА (ДОХОДЫ)', amount: `+ ${formatMoney(metrics.totalIncome)}`, pct: '100.0%', col: '#059669', isBold: true, isSummary: true },
    { label: 'ФОТ (Зарплаты и сдельная оплата сотрудников)', amount: `− ${formatMoney(metrics.payrollTotal)}`, pct: `${metrics.totalIncome > 0 ? ((metrics.payrollTotal / metrics.totalIncome) * 100).toFixed(1) : 0}%`, col: '#D97706', isBold: false },
    { label: 'Постоянные расходы (Аренда, коммуналка, интернет)', amount: `− ${formatMoney(metrics.fixedPaidExpenses)}`, pct: `${metrics.totalIncome > 0 ? ((metrics.fixedPaidExpenses / metrics.totalIncome) * 100).toFixed(1) : 0}%`, col: '#DC2626', isBold: false },
    { label: 'Переменные расходы (Упаковка, хозтовары, расходники)', amount: `− ${formatMoney(metrics.variableExpenses)}`, pct: `${metrics.totalIncome > 0 ? ((metrics.variableExpenses / metrics.totalIncome) * 100).toFixed(1) : 0}%`, col: '#DC2626', isBold: false },
    { label: 'Налог УСН (6% от выручки)', amount: `− ${formatMoney(metrics.taxAmount)}`, pct: `${metrics.totalIncome > 0 ? ((metrics.taxAmount / metrics.totalIncome) * 100).toFixed(1) : 0}%`, col: '#2563EB', isBold: false },
    ...(metrics.totalPartnerCommission > 0 ? [{ label: 'Расходы на партнёров (комиссионные отчисления)', amount: `− ${formatMoney(metrics.totalPartnerCommission)}`, pct: `${metrics.totalIncome > 0 ? ((metrics.totalPartnerCommission / metrics.totalIncome) * 100).toFixed(1) : 0}%`, col: '#7C3AED', isBold: false }] : []),
    { label: 'ИТОГО РАСХОДЫ', amount: `− ${formatMoney(metrics.totalExpensesAll)}`, pct: `${metrics.totalIncome > 0 ? ((metrics.totalExpensesAll / metrics.totalIncome) * 100).toFixed(1) : 0}%`, col: '#DC2626', isBold: true, isSummary: true },
    { label: 'ЧИСТАЯ ПРИБЫЛЬ (ОПЕРАЦИОННЫЙ РЕЗУЛЬТАТ)', amount: formatMoney(metrics.actualFreeCashFlow), pct: `${metrics.profitMarginPct.toFixed(1)}%`, col: metrics.actualFreeCashFlow >= 0 ? '#059669' : '#DC2626', isBold: true, isTotal: true }
  ]

  const rowH = 36
  plRows.forEach((r, idx) => {
    if (r.isTotal) {
      ctx1.fillStyle = metrics.actualFreeCashFlow >= 0 ? '#ECFDF5' : '#FEF2F2'
      ctx1.fillRect(marginX, curY, tableW, rowH)
    } else if (r.isSummary) {
      ctx1.fillStyle = '#F8FAFC'
      ctx1.fillRect(marginX, curY, tableW, rowH)
    } else if (idx % 2 === 1) {
      ctx1.fillStyle = '#FBFBFC'
      ctx1.fillRect(marginX, curY, tableW, rowH)
    }

    ctx1.strokeStyle = '#E2E8F0'
    ctx1.lineWidth = 1
    ctx1.strokeRect(marginX, curY, tableW, rowH)

    ctx1.fillStyle = r.isBold ? '#0F172A' : '#334155'
    ctx1.font = r.isBold ? 'bold 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif' : '13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
    ctx1.fillText(r.label, marginX + 16, curY + 23)

    ctx1.fillStyle = r.col
    ctx1.font = r.isBold ? 'bold 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif' : '600 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
    ctx1.fillText(r.amount, marginX + 700, curY + 23)

    ctx1.fillStyle = '#64748B'
    ctx1.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
    ctx1.fillText(r.pct, marginX + 900, curY + 23)

    curY += rowH
  })

  curY += 32

  // Section 2: Структура расходов (диаграмма затрат)
  ctx1.fillStyle = '#0F172A'
  ctx1.font = 'bold 18px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
  ctx1.fillText('2. Структура затрат и расходов', marginX, curY)

  curY += 24

  const barH = 26
  ctx1.fillStyle = '#F1F5F9'
  drawRoundedRect(ctx1, marginX, curY, tableW, barH, 8)
  ctx1.fill()

  let segX = marginX
  const segments = [
    { label: 'ФОТ (Зарплаты)', col: '#F59E0B', val: metrics.payrollTotal, pct: metrics.pctPayroll },
    { label: 'Постоянные расходы', col: '#10B981', val: metrics.fixedPaidExpenses, pct: metrics.pctFixed },
    { label: 'Переменные расходы', col: '#EF4444', val: metrics.variableExpenses, pct: metrics.pctVar },
    { label: 'Налог УСН (6%)', col: '#3B82F6', val: metrics.taxAmount, pct: metrics.pctTax },
    { label: 'Партнёры', col: '#8B5CF6', val: metrics.totalPartnerCommission, pct: metrics.pctPartner }
  ].filter(s => s.val > 0)

  segments.forEach((seg, sIdx) => {
    const sw = (seg.pct / 100) * tableW
    if (sw > 0) {
      ctx1.fillStyle = seg.col
      if (sIdx === 0 && segments.length === 1) {
        drawRoundedRect(ctx1, segX, curY, sw, barH, 8)
        ctx1.fill()
      } else {
        ctx1.fillRect(segX, curY, sw, barH)
      }
      segX += sw
    }
  })

  curY += barH + 18

  const legColW = tableW / Math.min(segments.length, 3)
  segments.forEach((seg, sIdx) => {
    const lx = marginX + (sIdx % 3) * legColW
    const ly = curY + Math.floor(sIdx / 3) * 26

    ctx1.fillStyle = seg.col
    drawRoundedRect(ctx1, lx, ly + 2, 12, 12, 3)
    ctx1.fill()

    ctx1.fillStyle = '#334155'
    ctx1.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
    ctx1.fillText(`${seg.label}: ${formatMoney(seg.val)} (${seg.pct.toFixed(1)}%)`, lx + 20, ly + 12)
  })

  // ==========================================
  // CONTINUATION PAGES: ДЕТАЛИЗАЦИЯ ФОТ И ПОСУТОЧНАЯ ДИНАМИКА
  // ==========================================
  const hasSalaryDetails = (metrics.salaryList || []).length > 0
  const hasDailyDetails = (metrics.flowChart || []).length > 0

  let activePage = null

  // --- SECTION 3: СВОДНАЯ ВЕДОМОСТЬ ФОТ ---
  if (hasSalaryDetails) {
    activePage = createPage(true)
    let pCtx = activePage.ctx
    let pY = activePage.curY

    const drawSalaryHeader = (isCont = false) => {
      pCtx.fillStyle = '#0F172A'
      pCtx.font = 'bold 18px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
      pCtx.fillText(isCont ? '3. Сводная ведомость ФОТ по сотрудникам (продолжение)' : '3. Сводная ведомость ФОТ по сотрудникам', marginX, pY)

      pY += 22

      pCtx.fillStyle = '#F1F5F9'
      drawRoundedRect(pCtx, marginX, pY, tableW, 36, 6)
      pCtx.fill()

      pCtx.fillStyle = '#475569'
      pCtx.font = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
      pCtx.fillText('СОТРУДНИК', marginX + 16, pY + 23)
      pCtx.fillText('ДОЛЖНОСТЬ', marginX + 280, pY + 23)
      pCtx.fillText('СМЕН', marginX + 460, pY + 23)
      pCtx.fillText('ЧАСОВ', marginX + 540, pY + 23)
      pCtx.fillText('ОКЛАД / БАЗА', marginX + 640, pY + 23)
      pCtx.fillText('БОНУСЫ / ШТРАФЫ', marginX + 800, pY + 23)
      pCtx.fillText('К ВЫПЛАТЕ', marginX + 960, pY + 23)

      pY += 36
    }

    drawSalaryHeader(false)

    const salRowH = 34
    metrics.salaryList.forEach((sal, idx) => {
      if (pY + salRowH > maxContentY) {
        activePage = createPage(true)
        pCtx = activePage.ctx
        pY = activePage.curY
        drawSalaryHeader(true)
      }

      if (idx % 2 === 1) {
        pCtx.fillStyle = '#FBFBFC'
        pCtx.fillRect(marginX, pY, tableW, salRowH)
      }
      pCtx.strokeStyle = '#E2E8F0'
      pCtx.strokeRect(marginX, pY, tableW, salRowH)

      pCtx.fillStyle = '#0F172A'
      pCtx.font = '600 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
      pCtx.fillText(sal.employeeName || 'Сотрудник', marginX + 16, pY + 22)

      pCtx.fillStyle = '#64748B'
      pCtx.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
      pCtx.fillText(sal.role || 'Оператор', marginX + 280, pY + 22)
      pCtx.fillText(`${sal.shiftsCount || sal.daysWorked || 0}`, marginX + 460, pY + 22)
      pCtx.fillText(`${sal.workHours || sal.hoursWorked || 0} ч`, marginX + 540, pY + 22)
      pCtx.fillText(formatMoney(sal.baseAmount || sal.base || 0), marginX + 640, pY + 22)

      const netBonus = Number(sal.bonusAmount || sal.bonuses || 0) - Number(sal.deductionsAmount || sal.deductions || 0)
      pCtx.fillStyle = netBonus >= 0 ? '#059669' : '#DC2626'
      pCtx.fillText(netBonus !== 0 ? (netBonus > 0 ? `+${formatMoney(netBonus)}` : formatMoney(netBonus)) : '—', marginX + 800, pY + 22)

      pCtx.fillStyle = '#0F172A'
      pCtx.font = 'bold 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
      pCtx.fillText(formatMoney(sal.total || 0), marginX + 960, pY + 22)

      pY += salRowH
    })

    // Итоговая строка по ФОТ
    const salTotalH = 38
    if (pY + salTotalH > maxContentY) {
      activePage = createPage(true)
      pCtx = activePage.ctx
      pY = activePage.curY
      drawSalaryHeader(true)
    }
    pCtx.fillStyle = '#F8FAFC'
    pCtx.fillRect(marginX, pY, tableW, salTotalH)
    pCtx.strokeStyle = '#CBD5E1'
    pCtx.lineWidth = 1.5
    pCtx.strokeRect(marginX, pY, tableW, salTotalH)

    pCtx.fillStyle = '#0F172A'
    pCtx.font = 'bold 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
    pCtx.fillText('ИТОГО ФОТ', marginX + 16, pY + 24)
    pCtx.fillText(formatMoney(metrics.payrollTotal), marginX + 960, pY + 24)

    pY += salTotalH
    activePage.curY = pY + 34
  }

  // --- SECTION 4: ПОСУТОЧНАЯ ДИНАМИКА ДЕНЕЖНОГО ПОТОКА ---
  if (hasDailyDetails) {
    const neededInitialH = 22 + 36 + 32 * 2
    if (!activePage || activePage.curY + neededInitialH > maxContentY) {
      activePage = createPage(true)
    }

    let pCtx = activePage.ctx
    let pY = activePage.curY

    const drawDailyHeader = (isCont = false) => {
      pCtx.fillStyle = '#0F172A'
      pCtx.font = 'bold 18px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
      pCtx.fillText(isCont ? '4. Посуточная динамика денежного потока (продолжение)' : '4. Посуточная динамика денежного потока', marginX, pY)

      pY += 22

      pCtx.fillStyle = '#F1F5F9'
      drawRoundedRect(pCtx, marginX, pY, tableW, 36, 6)
      pCtx.fill()

      pCtx.fillStyle = '#475569'
      pCtx.font = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
      pCtx.fillText('ДАТА И ДЕНЬ НЕДЕЛИ', marginX + 16, pY + 23)
      pCtx.fillText('ДОХОДЫ (РУБ)', marginX + 320, pY + 23)
      pCtx.fillText('РАСХОДЫ (РУБ)', marginX + 580, pY + 23)
      pCtx.fillText('ЧИСТАЯ ПРИБЫЛЬ (РУБ)', marginX + 840, pY + 23)

      pY += 36
    }

    drawDailyHeader(false)

    const dayRowH = 32
    metrics.flowChart.forEach((day, idx) => {
      if (pY + dayRowH > maxContentY) {
        activePage = createPage(true)
        pCtx = activePage.ctx
        pY = activePage.curY
        drawDailyHeader(true)
      }

      if (idx % 2 === 1) {
        pCtx.fillStyle = '#FBFBFC'
        pCtx.fillRect(marginX, pY, tableW, dayRowH)
      }
      pCtx.strokeStyle = '#E2E8F0'
      pCtx.strokeRect(marginX, pY, tableW, dayRowH)

      pCtx.fillStyle = '#334155'
      pCtx.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
      pCtx.fillText(day.displayDate || formatDateRu(day.date), marginX + 16, pY + 21)

      pCtx.fillStyle = '#059669'
      pCtx.fillText(`+ ${formatMoney(day.income)}`, marginX + 320, pY + 21)

      pCtx.fillStyle = '#DC2626'
      pCtx.fillText(`− ${formatMoney(day.expense)}`, marginX + 580, pY + 21)

      const pVal = day.realProfit !== undefined ? day.realProfit : (day.income - day.expense)
      pCtx.fillStyle = pVal >= 0 ? '#059669' : '#DC2626'
      pCtx.font = 'bold 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
      pCtx.fillText(formatMoney(pVal), marginX + 840, pY + 21)

      pY += dayRowH
    })

    // Итоговая строка посуточной динамики (100% совпадение со сводкой)
    const dayTotalH = 38
    if (pY + dayTotalH > maxContentY) {
      activePage = createPage(true)
      pCtx = activePage.ctx
      pY = activePage.curY
      drawDailyHeader(true)
    }
    pCtx.fillStyle = '#F8FAFC'
    pCtx.fillRect(marginX, pY, tableW, dayTotalH)
    pCtx.strokeStyle = '#CBD5E1'
    pCtx.lineWidth = 1.5
    pCtx.strokeRect(marginX, pY, tableW, dayTotalH)

    pCtx.fillStyle = '#0F172A'
    pCtx.font = 'bold 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
    pCtx.fillText('ИТОГО ЗА ПЕРИОД', marginX + 16, pY + 24)

    pCtx.fillStyle = '#059669'
    pCtx.fillText(`+ ${formatMoney(metrics.totalIncome)}`, marginX + 320, pY + 24)

    pCtx.fillStyle = '#DC2626'
    pCtx.fillText(`− ${formatMoney(metrics.totalExpensesAll)}`, marginX + 580, pY + 24)

    pCtx.fillStyle = metrics.actualFreeCashFlow >= 0 ? '#059669' : '#DC2626'
    pCtx.font = 'bold 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
    pCtx.fillText(formatMoney(metrics.actualFreeCashFlow), marginX + 840, pY + 24)

    pY += dayTotalH
    activePage.curY = pY
  }

  // ==========================================
  // PASS 2: STAMP FOOTERS ON ALL PAGES WITH EXACT TOTAL PAGES
  // ==========================================
  const totalPages = pages.length

  pages.forEach((pageObj, idx) => {
    const fCtx = pageObj.ctx

    // Footer divider line
    fCtx.strokeStyle = '#E2E8F0'
    fCtx.lineWidth = 1
    fCtx.beginPath()
    fCtx.moveTo(marginX, H - 55)
    fCtx.lineTo(W - marginX, H - 55)
    fCtx.stroke()

    // Brand and page count
    fCtx.fillStyle = '#64748B'
    fCtx.font = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
    fCtx.fillText('Сформировано в сервисе «ПВЗ Расчёт» • pvzraschet.ru', marginX, H - 35)

    const pageLabel = `Страница ${idx + 1} из ${totalPages}`
    fCtx.fillText(pageLabel, W - marginX - 120, H - 35)
  })

  // ==========================================
  // ASSEMBLE PDF VIA jsPDF
  // ==========================================
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true
  })

  pages.forEach((pageObj, idx) => {
    if (idx > 0) doc.addPage()
    // Using JPEG at 0.88 quality provides razor-sharp Cyrillic typography while keeping 365-day PDFs small
    const imgData = pageObj.canvas.toDataURL('image/jpeg', 0.88)
    doc.addImage(imgData, 'JPEG', 0, 0, 210, 297, undefined, 'FAST')
  })

  const fileName = `PVZ_Raschet_Analytics_${period.startDate}_${period.endDate}.pdf`

  if (!returnDocOnly) {
    doc.save(fileName)
  }

  return { fileName, doc, totalPages }
}

function drawRoundedRect(ctx, x, y, width, height, radius) {
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.lineTo(x + width - radius, y)
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
  ctx.lineTo(x + width, y + height - radius)
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
  ctx.lineTo(x + radius, y + height)
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
  ctx.lineTo(x, y + radius)
  ctx.quadraticCurveTo(x, y, x + radius, y)
  ctx.closePath()
}

